import React, { useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';
import {
  AlertTriangle,
  Maximize2,
  Minimize2,
  Plus,
  RefreshCw,
  RotateCcw,
  Send,
  X,
} from 'lucide-react';
import { DateTime } from 'luxon';
import { queryCache, useMutation, useQuery } from 'react-query';
import { DotBricksLoading, Toast } from 'src/common/components';

import {
  cancelEdition,
  fetchAccountCohorts,
  fetchCandidates,
  fetchEdition,
  fetchEditionRecipientsSummary,
  fetchPreviewHtml,
  markEditionReady,
  patchEdition,
  publishBackfill,
  sendNow,
  sendTestEmail,
  setEditionAccountCohort,
} from '../api';
import {
  ACCOUNT_COHORTS_QUERY_KEY,
  CANDIDATES_QUERY_KEY,
  EDITION_AUDIENCE_LABEL,
  EDITION_QUERY_KEY,
  EDITION_RECIPIENTS_SUMMARY_QUERY_KEY,
  EDITIONS_QUERY_KEY,
  EMAIL_PREVIEW_SANDBOX,
  RENDER_SLOT_LABEL,
  RENDER_SLOT_ORDER,
} from '../constants';
import { CandidateRenderSlot, DigestEditionCandidate, EditionPatchPayload } from '../types';
import {
  describeLegacyAccountFilter,
  getEditionActionErrorMessage,
  openPreviewLinksInNewTab,
  pluralizeAccounts,
  pluralizePeople,
  pluralizeUnsubscribed,
} from '../utils';

import AccountCohortFormModal from './AccountCohortFormModal';
import AddCandidateForm from './AddCandidateForm';
import CandidateCard from './CandidateCard';
import CandidateReadinessIssues from './CandidateReadinessIssues';
import CatalogPickerModal from './CatalogPickerModal';
import { EditionStatusBadge } from './EditionStatusBadge';

type EditionComposerProps = {
  editionId: string;
  onArchive?: () => void;
};

const EditionComposer: React.FC<EditionComposerProps> = ({ editionId, onArchive }) => {
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [editionFields, setEditionFields] = useState<EditionPatchPayload>({});
  const [isEditionDirty, setIsEditionDirty] = useState(false);
  const [headlineIndex, setHeadlineIndex] = useState(0);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showCatalogPicker, setShowCatalogPicker] = useState(false);
  const [showBackfillPanel, setShowBackfillPanel] = useState(false);
  const [backfillDate, setBackfillDate] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [editorExpanded, setEditorExpanded] = useState(false);

  // The account cohort picked in the select but not applied yet; null = untouched.
  const [pickedAccountCohortId, setPickedAccountCohortId] = useState<string | null>(null);
  const [showAccountCohortModal, setShowAccountCohortModal] = useState(false);

  const { data: editionData, isLoading: isLoadingEdition } = useQuery(
    [EDITION_QUERY_KEY, editionId],
    fetchEdition,
    { enabled: !!editionId }
  );

  const { data: candidatesData, isLoading: isLoadingCandidates } = useQuery(
    [CANDIDATES_QUERY_KEY, editionId],
    fetchCandidates,
    { enabled: !!editionId }
  );

  const { data: accountCohortsData } = useQuery([ACCOUNT_COHORTS_QUERY_KEY], fetchAccountCohorts);

  const edition = editionData?.data;
  const allCandidates = candidatesData?.data.results ?? [];
  const accountCohorts = accountCohortsData?.data.results ?? [];

  const savedAccountCohortId = edition?.account_cohort ?? null;
  // Editions made before account cohorts keep the filter they carry on themselves.
  const isLegacyAccounts =
    !!edition && savedAccountCohortId === null && edition.cohort_mode !== null;
  const hasAccounts = savedAccountCohortId !== null || isLegacyAccounts;

  const { data: summaryData } = useQuery(
    [EDITION_RECIPIENTS_SUMMARY_QUERY_KEY, editionId],
    fetchEditionRecipientsSummary,
    { enabled: !!editionId && hasAccounts }
  );

  const savedAccountCohortValue = savedAccountCohortId === null ? '' : String(savedAccountCohortId);
  const defaultAccountCohortId = accountCohorts.find((cohort) => cohort.is_protected)?.id;
  // A legacy edition keeps its own filter until someone picks a cohort; a fresh one
  // starts on "Todas las cuentas activas", which still has to be applied.
  const defaultAccountCohortValue =
    isLegacyAccounts || defaultAccountCohortId === undefined ? '' : String(defaultAccountCohortId);
  const selectedAccountCohortValue =
    pickedAccountCohortId ??
    (savedAccountCohortId === null ? defaultAccountCohortValue : savedAccountCohortValue);
  const isAccountsDirty = !!edition && selectedAccountCohortValue !== savedAccountCohortValue;

  // Reset local form when navigating to a different edition.
  // Intentionally omit `edition` from deps — we only want to re-run when the
  // ID changes, not on every server re-fetch (which would overwrite local edits).
  useEffect(() => {
    if (edition) {
      // Empty eyebrow/subheading are left empty on purpose: the backend then falls
      // back to a default based on the edition's send date, not today's.
      setEditionFields({
        titulo: edition.titulo,
        eyebrow_override: edition.eyebrow_override ?? '',
        subheading_override: edition.subheading_override ?? '',
        scheduled_send_at: edition.scheduled_send_at ?? undefined,
      });
      setPickedAccountCohortId(null);
      setIsEditionDirty(false);
    }
  }, [edition?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateEditionField = <K extends keyof EditionPatchPayload>(
    key: K,
    value: EditionPatchPayload[K]
  ): void => {
    setEditionFields((prev) => ({ ...prev, [key]: value }));
    setIsEditionDirty(true);
  };

  const refreshPreview = useCallback(async () => {
    if (!editionId) {
      return;
    }
    setIsLoadingPreview(true);
    setPreviewError('');
    try {
      const html = await fetchPreviewHtml(editionId);
      setPreviewHtml(html);
      setPreviewError('');
    } catch {
      setPreviewError('No se pudo cargar el preview.');
    } finally {
      setIsLoadingPreview(false);
    }
  }, [editionId]);

  const [saveEdition, { isLoading: isSavingEdition }] = useMutation(
    () => patchEdition(editionId ?? '', editionFields),
    {
      onSuccess: () => {
        setIsEditionDirty(false);
        void queryCache.invalidateQueries([EDITION_QUERY_KEY, editionId]);
        void refreshPreview();
        Toast.success('Edición guardada.');
      },
      onError: () => {
        Toast.error('Error al guardar la edición.');
      },
    }
  );

  const [triggerSend, { isLoading: isSending }] = useMutation(() => sendNow(editionId ?? ''), {
    onSuccess: () => {
      void queryCache.invalidateQueries([EDITION_QUERY_KEY, editionId]);
      Toast.success('Edición enviada.');
    },
    onError: () => {
      Toast.error('Error al enviar la edición.');
    },
  });

  const [applyAccounts, { isLoading: isSettingAccounts }] = useMutation(
    () => setEditionAccountCohort(editionId ?? '', Number(selectedAccountCohortValue)),
    {
      onSuccess: () => {
        void queryCache.invalidateQueries([EDITION_QUERY_KEY, editionId]);
        void queryCache.invalidateQueries([EDITION_RECIPIENTS_SUMMARY_QUERY_KEY, editionId]);
        void refreshPreview();
        Toast.success('Cuentas actualizadas.');
      },
      onError: (error) => {
        Toast.error(getEditionActionErrorMessage(error, 'No se pudieron aplicar las cuentas.'));
      },
    }
  );

  const [doPublishBackfill, { isLoading: isPublishingBackfill }] = useMutation(
    // Input type="date" gives local "YYYY-MM-DD"; backend DateTimeField needs full ISO datetime.
    () =>
      publishBackfill(
        editionId ?? '',
        backfillDate ? DateTime.fromISO(backfillDate).startOf('day').toISO() : null
      ),
    {
      onSuccess: () => {
        void queryCache.invalidateQueries([EDITION_QUERY_KEY, editionId]);
        void queryCache.invalidateQueries(EDITIONS_QUERY_KEY);
        setShowBackfillPanel(false);
        Toast.success('Edición publicada como lanzamiento histórico.');
      },
      onError: () => {
        Toast.error('Error al publicar el lanzamiento.');
      },
    }
  );

  const [doMarkReady, { isLoading: isMarkingReady }] = useMutation(
    () => markEditionReady(editionId ?? ''),
    {
      onSuccess: () => {
        void queryCache.invalidateQueries([EDITION_QUERY_KEY, editionId]);
        void queryCache.invalidateQueries(EDITIONS_QUERY_KEY);
        Toast.success('Edición marcada como lista para enviar (APPROVED).');
      },
      onError: (error) => {
        Toast.error(
          getEditionActionErrorMessage(
            error,
            'No se pudo marcar la edición como lista para enviar.'
          )
        );
      },
    }
  );

  const [doCancelSend, { isLoading: isCancelling }] = useMutation(
    () => cancelEdition(editionId ?? ''),
    {
      onSuccess: () => {
        void queryCache.invalidateQueries([EDITION_QUERY_KEY, editionId]);
        void queryCache.invalidateQueries(EDITIONS_QUERY_KEY);
        Toast.success('Envío cancelado. La edición volvió a Borrador.');
      },
      onError: () => {
        Toast.error('Error al cancelar el envío.');
      },
    }
  );

  const [doSendTest, { isLoading: isSendingTest }] = useMutation(
    () => sendTestEmail(editionId ?? '', testEmail),
    {
      onSuccess: () => {
        setShowTestPanel(false);
        Toast.success(`Email de prueba enviado a ${testEmail}.`);
      },
      onError: () => {
        Toast.error('Error al enviar el email de prueba.');
      },
    }
  );

  const rotateHeadline = (): void => {
    const headlines = allCandidates
      .map((c) => c.headline_override)
      .filter((h) => h.trim().length > 0);
    if (headlines.length === 0) {
      return;
    }
    const next = headlines[headlineIndex % headlines.length];
    setHeadlineIndex((i) => i + 1);
    setEditionFields((prev) => ({ ...prev, titulo: next }));
    setIsEditionDirty(true);
  };

  // Load preview on mount
  useEffect(() => {
    void refreshPreview();
  }, [refreshPreview]);

  const candidatesBySlot = RENDER_SLOT_ORDER.reduce<
    Record<CandidateRenderSlot, DigestEditionCandidate[]>
  >(
    (acc, slot) => ({
      ...acc,
      [slot]: allCandidates.filter((c) => c.render_slot === slot),
    }),
    { hero: [], card: [], learn_more: [] }
  );

  const unrejectedWithIssues = allCandidates.filter(
    (c) => c.approval_status !== 'rejected' && (c.readiness_issues ?? []).length > 0
  );
  // A feature that went Legacy/Discontinued after it was added stays as a candidate, is left
  // out of the email and blocks "lista para enviar" until the candidate is rejected.
  const isInactiveFeature = (c: DigestEditionCandidate): boolean =>
    !!c.readiness_issues?.includes('feature_not_active');
  const inactiveFeatureCandidates = unrejectedWithIssues.filter(isInactiveFeature);
  const candidatesWithIssues = unrejectedWithIssues.filter((c) => !isInactiveFeature(c));

  const isLoading = isLoadingEdition || isLoadingCandidates;

  if (isLoading) {
    return <DotBricksLoading />;
  }
  if (!edition) {
    return <p className="p-8 text-gray-500">Edición no encontrada.</p>;
  }

  const isDraft = edition.status === 'draft';
  const isApproved = edition.status === 'approved';
  const isSent = edition.status === 'sent';
  const isBusy =
    isSavingEdition ||
    isSending ||
    isSettingAccounts ||
    isPublishingBackfill ||
    isCancelling ||
    isSendingTest ||
    isMarkingReady;
  const tituloEmpty = !(editionFields.titulo ?? '').trim();
  const accountsDirtyMessage = 'Aplica las cuentas antes de enviar o aprobar la edición';
  const canEditAccounts = isDraft || edition.status === 'pending_approval';
  const legacyAccountsSummary = isLegacyAccounts ? describeLegacyAccountFilter(edition) : null;

  let sendNowTitle = 'Enviar la edición ahora';
  if (tituloEmpty) {
    sendNowTitle = 'El asunto no puede estar vacío';
  } else if (isAccountsDirty) {
    sendNowTitle = accountsDirtyMessage;
  }
  const isSendNowBlocked = tituloEmpty || isAccountsDirty;

  const scheduledDate = edition.scheduled_send_at
    ? new Date(edition.scheduled_send_at).toLocaleString('es-PR', {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : null;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-3 border-b bg-white-0.0 flex-shrink-0">
        <h2 className="flex-1 text-base font-semibold truncate">
          {edition.titulo || <span className="italic text-gray-400">Sin título</span>}
        </h2>
        <EditionStatusBadge status={edition.status} />
        <span className="flex-shrink-0 rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700 border border-indigo-200">
          {edition.cohort_name}
        </span>
        {scheduledDate && !isSent && (
          <span className="flex-shrink-0 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 border border-blue-200">
            Envío: {scheduledDate}
          </span>
        )}
        <button
          type="button"
          className="btn btn-ghost hover:bg-green-50 px-3 py-1 text-sm text-green-700 border border-green-200"
          disabled={isBusy}
          onClick={() => {
            setShowTestPanel((v) => !v);
            setShowBackfillPanel(false);
          }}
        >
          Enviar prueba
        </button>
        {!isSent && (
          <button
            type="button"
            className="btn btn-ghost hover:bg-blue-50 px-3 py-1 text-sm text-blue-700 border border-blue-200"
            disabled={isBusy}
            onClick={() => {
              setShowBackfillPanel((v) => !v);
              setShowTestPanel(false);
            }}
          >
            Publicar como lanzamiento
          </button>
        )}
        {isDraft && (
          <button
            type="button"
            className="btn btn-ghost hover:bg-indigo-50 px-3 py-1 text-sm text-indigo-700 border border-indigo-200"
            disabled={isBusy || tituloEmpty || isAccountsDirty}
            title={
              isAccountsDirty
                ? accountsDirtyMessage
                : 'Requiere candidatos aprobados y cuentas asignadas'
            }
            onClick={() => doMarkReady()}
          >
            {isMarkingReady ? 'Aprobando…' : 'Marcar lista para enviar'}
          </button>
        )}
        {!isSent && !isDraft && (
          <button
            type="button"
            className="btn btn-ghost hover:bg-red-50 px-3 py-1 text-sm text-red-600"
            disabled={isBusy}
            title="Revierte a Borrador y limpia la fecha de envío"
            onClick={() => doCancelSend()}
          >
            Cancelar envío
          </button>
        )}
        {onArchive && !isSent && (
          <button
            type="button"
            className="btn btn-ghost hover:text-gray-800 px-3 py-1 text-sm text-gray-500"
            disabled={isBusy}
            onClick={onArchive}
          >
            Archivar
          </button>
        )}
      </div>

      {!isSent && inactiveFeatureCandidates.length > 0 && (
        <div
          role="alert"
          className="bg-red-50 flex-shrink-0 px-6 py-2 text-sm text-red-800 border-b"
        >
          <p className="flex items-center gap-2 font-medium">
            <AlertTriangle size={14} className="flex-shrink-0" />
            {inactiveFeatureCandidates.length === 1
              ? '1 candidato ya no está vigente'
              : `${inactiveFeatureCandidates.length} candidatos ya no están vigentes`}
            : su feature pasó a Legacy o Descontinuado, no saldrá en el correo y la edición no se
            puede marcar lista hasta que lo rechaces.
          </p>
          <ul className="mt-1 text-xs list-disc list-inside">
            {inactiveFeatureCandidates.map((candidate) => (
              <li key={candidate.id}>{candidate.headline_override || 'Sin título'}</li>
            ))}
          </ul>
        </div>
      )}

      {!isSent && candidatesWithIssues.length > 0 && (
        <details className="bg-amber-50 flex-shrink-0 px-6 py-2 border-b">
          <summary className="text-amber-800 flex items-center gap-2 text-sm font-medium cursor-pointer">
            <AlertTriangle size={14} className="flex-shrink-0" />
            {candidatesWithIssues.length === 1
              ? '1 feature todavía no está listo para verse bien'
              : `${candidatesWithIssues.length} features todavía no están listos para verse bien`}
            <span className="text-amber-700 text-xs font-normal">
              (son avisos: igual puedes aprobar y enviar)
            </span>
          </summary>
          <ul className="mt-2 space-y-2">
            {candidatesWithIssues.map((candidate) => (
              <li key={candidate.id} className="space-y-1">
                <p className="text-xs font-semibold text-gray-700">
                  {candidate.headline_override || (
                    <span className="italic text-gray-400">Sin título</span>
                  )}
                </p>
                <CandidateReadinessIssues candidate={candidate} />
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* Backfill panel */}
      {showBackfillPanel && !isSent && (
        <div className="bg-blue-50 flex items-center flex-shrink-0 gap-3 px-6 py-3 border-b">
          <p className="text-sm font-medium text-blue-800">Publicar sin enviar email:</p>
          <input
            type="date"
            className="input px-2 py-1 text-sm"
            value={backfillDate}
            onChange={(e) => setBackfillDate(e.target.value)}
          />
          <button
            type="button"
            className="btn btn-primary px-3 py-1 text-sm"
            disabled={isBusy}
            onClick={() => doPublishBackfill()}
          >
            {isPublishingBackfill ? 'Publicando…' : 'Confirmar lanzamiento'}
          </button>
          <button
            type="button"
            className="btn btn-ghost px-2 py-1 text-sm text-gray-500"
            onClick={() => setShowBackfillPanel(false)}
          >
            Cancelar
          </button>
        </div>
      )}

      {/* Test email panel */}
      {showTestPanel && (
        <div className="bg-green-50 flex items-center flex-shrink-0 gap-3 px-6 py-3 border-b">
          <p className="text-sm font-medium text-green-800">Enviar email de prueba a:</p>
          <input
            type="email"
            className="input px-2 py-1 text-sm"
            placeholder="email@ejemplo.com"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
          />
          <button
            type="button"
            className="btn btn-primary px-3 py-1 text-sm"
            disabled={isBusy || !testEmail}
            onClick={() => doSendTest()}
          >
            {isSendingTest ? 'Enviando…' : 'Enviar'}
          </button>
          <button
            type="button"
            className="btn btn-ghost p-1 text-gray-400"
            onClick={() => setShowTestPanel(false)}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Split pane */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: edition fields + candidates */}
        <div
          className={classNames(
            'bg-gray-50 flex flex-col overflow-y-auto border-r transition-all',
            previewExpanded ? 'w-0 overflow-hidden' : editorExpanded ? 'flex-1' : 'w-2/5'
          )}
        >
          {/* Editor expand toggle */}
          <div className="flex justify-end px-2 py-1 bg-white border-b">
            <button
              type="button"
              className="btn btn-ghost p-1.5"
              title={editorExpanded ? 'Mostrar preview' : 'Expandir editor'}
              onClick={() => {
                setEditorExpanded((v) => !v);
                setPreviewExpanded(false);
              }}
            >
              {editorExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
          </div>
          {/* Edition-level fields */}
          <div className="space-y-3 border-b bg-white-0.0 px-4 py-4">
            <h3 className="text-xs font-semibold tracking-widest text-gray-400 uppercase">
              Edición
            </h3>

            <label className="block">
              <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                Asunto / título del email *
              </span>
              <div className="mt-0.5 flex items-center gap-1">
                <input
                  className={classNames(
                    'input flex-1 text-sm',
                    tituloEmpty && 'border-red-300 focus:ring-red-300'
                  )}
                  value={editionFields.titulo ?? ''}
                  placeholder="El gran título de esta edición"
                  onChange={(e) => updateEditionField('titulo', e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-ghost px-2 py-1"
                  title="Rotar headline desde los candidatos"
                  onClick={rotateHeadline}
                >
                  <RotateCcw size={14} />
                </button>
              </div>
              {tituloEmpty && (
                <p className="mt-0.5 text-xs text-red-500">
                  Requerido — el asunto no puede estar vacío.
                </p>
              )}
            </label>

            <label className="block">
              <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                Eyebrow (encima del título, opcional)
              </span>
              <input
                className="input mt-0.5 w-full text-sm"
                value={editionFields.eyebrow_override ?? ''}
                placeholder="Vacío = Nuevo en BrainHi · <mes y año del envío>"
                onChange={(e) => updateEditionField('eyebrow_override', e.target.value)}
              />
            </label>

            <label className="block">
              <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                Intro / subheading (opcional)
              </span>
              <textarea
                className="input mt-0.5 w-full resize-none text-sm"
                rows={3}
                value={editionFields.subheading_override ?? ''}
                placeholder="Hola {first_name}, esto es lo nuevo en BrainHi para {account_name}…"
                onChange={(e) => updateEditionField('subheading_override', e.target.value)}
              />
            </label>

            <label className="block">
              <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                Envío programado
              </span>
              <input
                className="input mt-0.5 w-full text-sm"
                type="datetime-local"
                value={
                  editionFields.scheduled_send_at
                    ? DateTime.fromISO(editionFields.scheduled_send_at).toFormat(
                        "yyyy-MM-dd'T'HH:mm"
                      )
                    : ''
                }
                onChange={(e) =>
                  updateEditionField(
                    'scheduled_send_at',
                    e.target.value ? DateTime.fromISO(e.target.value).toISO() : null
                  )
                }
              />
            </label>

            {/* Save button — primary, always visible */}
            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="btn btn-primary px-4 py-1.5 text-sm"
                  disabled={!isEditionDirty || isBusy || tituloEmpty}
                  onClick={() => saveEdition()}
                >
                  {isSavingEdition ? 'Guardando…' : 'Guardar cambios'}
                </button>
                {/* Send now — discrete, only for approved editions */}
                {isApproved && (
                  <button
                    type="button"
                    className={classNames(
                      'flex items-center gap-1 text-sm px-3 py-1.5 rounded transition-colors',
                      isSendNowBlocked
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-500 hover:text-green-700 hover:bg-green-50'
                    )}
                    disabled={isBusy || isSendNowBlocked}
                    title={sendNowTitle}
                    onClick={() => triggerSend()}
                  >
                    <Send size={13} />
                    {isSending ? 'Enviando…' : 'Enviar ahora'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Accounts section */}
          <div className="space-y-3 border-b bg-white-0.0 px-4 py-4">
            <h3 className="text-xs font-semibold tracking-widest text-gray-400 uppercase">
              Cuentas
            </h3>

            {legacyAccountsSummary && (
              <div className="bg-gray-50 p-2 text-xs text-gray-500 rounded">
                <span className="font-medium">Configuración anterior: </span>
                {legacyAccountsSummary}
              </div>
            )}

            <label className="block">
              <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                Cohort de cuentas
              </span>
              <select
                className="input mt-0.5 w-full text-sm"
                value={selectedAccountCohortValue}
                onChange={(e) => setPickedAccountCohortId(e.target.value)}
                disabled={isBusy || !canEditAccounts}
              >
                {selectedAccountCohortValue === '' && (
                  <option value="" disabled>
                    Selecciona un cohort de cuentas…
                  </option>
                )}
                {savedAccountCohortId !== null &&
                  !accountCohorts.some((cohort) => cohort.id === savedAccountCohortId) && (
                    <option value={savedAccountCohortValue}>
                      {edition.account_cohort_name ?? `Cohort #${savedAccountCohortId}`}
                    </option>
                  )}
                {accountCohorts.map((cohort) => (
                  <option key={cohort.id} value={String(cohort.id)}>
                    {cohort.name}
                  </option>
                ))}
              </select>
            </label>

            {!canEditAccounts && !isSent && (
              <p className="text-xs text-gray-400">
                Las cuentas solo se cambian antes de aprobar la edición. Cancela el envío para
                volver a Borrador.
              </p>
            )}

            <div className="flex items-center gap-3">
              <button
                type="button"
                className="btn btn-ghost px-3 py-1 text-xs border border-gray-200"
                disabled={
                  isBusy ||
                  !canEditAccounts ||
                  selectedAccountCohortValue === '' ||
                  !isAccountsDirty
                }
                onClick={() => applyAccounts()}
              >
                {isSettingAccounts ? 'Aplicando…' : 'Aplicar cuentas'}
              </button>
              {canEditAccounts && (
                <button
                  type="button"
                  className="hover:text-blue-800 text-xs font-medium text-blue-700 underline"
                  onClick={() => setShowAccountCohortModal(true)}
                >
                  Crear cohort de cuentas
                </button>
              )}
            </div>

            {summaryData ? (
              <p className="text-xs text-gray-600">
                Llega a {pluralizePeople(summaryData.data.people_count)} en{' '}
                {pluralizeAccounts(summaryData.data.account_count)} · excluye{' '}
                {pluralizeUnsubscribed(summaryData.data.unsubscribed_count)} de la lista{' '}
                {EDITION_AUDIENCE_LABEL[summaryData.data.send_list]}
              </p>
            ) : (
              !hasAccounts && (
                <p className="text-xs text-gray-400">
                  Todavía no hay cuentas aplicadas: elige un cohort de cuentas y pulsa "Aplicar
                  cuentas".
                </p>
              )
            )}
            {hasAccounts && isAccountsDirty && (
              <p className="text-amber-700 text-xs">
                Hay cambios sin aplicar; el resumen es el de las cuentas ya aplicadas.
              </p>
            )}
          </div>

          {/* Candidates grouped by slot */}
          <div className="px-4 py-4 space-y-6">
            {allCandidates.length === 0 && !showAddForm && (
              <p className="pt-4 text-sm text-center text-gray-500">
                Esta edición no tiene candidatos aún.
              </p>
            )}

            {RENDER_SLOT_ORDER.map((slot) => {
              const slotCandidates = candidatesBySlot[slot];
              if (slotCandidates.length === 0) {
                return null;
              }
              return (
                <div key={slot}>
                  <h3 className="mb-2 text-xs font-semibold tracking-widest text-gray-400 uppercase">
                    {RENDER_SLOT_LABEL[slot]}
                  </h3>
                  <div className="space-y-3">
                    {slotCandidates.map((candidate) => (
                      <CandidateCard
                        key={candidate.id}
                        candidate={candidate}
                        editionId={editionId ?? ''}
                        onSaved={refreshPreview}
                      />
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Add candidate form or button */}
            {showAddForm ? (
              <AddCandidateForm
                editionId={editionId ?? ''}
                onClose={() => setShowAddForm(false)}
                onAdded={refreshPreview}
              />
            ) : (
              <div className="space-y-2">
                <button
                  type="button"
                  className="w-full rounded-lg border-2 border-dashed border-blue-200 bg-blue-50 py-2.5 text-sm font-semibold text-blue-700 hover:bg-blue-100 transition-colors"
                  onClick={() => setShowCatalogPicker(true)}
                >
                  + Agregar del catálogo (PR scan)
                </button>
                <button
                  type="button"
                  className="btn btn-ghost flex w-full items-center justify-center gap-1.5 border border-dashed border-gray-300 py-2 text-sm text-gray-500 hover:border-gray-400 hover:text-gray-700"
                  onClick={() => setShowAddForm(true)}
                >
                  <Plus size={14} />
                  Agregar candidato manual
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right: live preview */}
        <div className="flex flex-col flex-1 overflow-hidden bg-gray-100">
          <div className="flex flex-shrink-0 items-center justify-between border-b bg-white-0.0 px-4 py-2">
            <span className="text-sm font-medium text-gray-600">Preview del email</span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="btn btn-ghost flex items-center gap-1 px-2 py-1 text-sm"
                disabled={isLoadingPreview}
                onClick={refreshPreview}
              >
                <RefreshCw size={14} className={isLoadingPreview ? 'animate-spin' : ''} />
                {isLoadingPreview ? 'Cargando…' : 'Refrescar'}
              </button>
              <button
                type="button"
                className="btn btn-ghost p-1.5"
                title={previewExpanded ? 'Mostrar editor' : 'Expandir preview'}
                onClick={() => {
                  setPreviewExpanded((v) => !v);
                  setEditorExpanded(false);
                }}
              >
                {previewExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              </button>
            </div>
          </div>

          <div className="relative flex-1 overflow-hidden">
            {isLoadingPreview && (
              <div className="bg-opacity-80 absolute inset-0 z-10 flex items-center justify-center bg-white">
                <DotBricksLoading />
              </div>
            )}
            {previewError && !previewHtml && (
              <div className="absolute inset-0 z-10 flex items-start p-6">
                <p className="bg-red-50 w-full p-3 text-sm text-red-600 rounded">{previewError}</p>
              </div>
            )}
            <iframe
              title="Email preview"
              srcDoc={previewHtml ? openPreviewLinksInNewTab(previewHtml) : undefined}
              className="w-full h-full border-0"
              sandbox={EMAIL_PREVIEW_SANDBOX}
            />
          </div>
        </div>
      </div>

      {showAccountCohortModal && (
        <AccountCohortFormModal
          onClose={() => setShowAccountCohortModal(false)}
          onSaved={(cohort) => {
            setPickedAccountCohortId(String(cohort.id));
            setShowAccountCohortModal(false);
          }}
        />
      )}

      {showCatalogPicker && editionId && (
        <CatalogPickerModal
          editionId={editionId}
          onClose={() => setShowCatalogPicker(false)}
          onAdded={() => {
            void queryCache.invalidateQueries([CANDIDATES_QUERY_KEY, editionId]);
          }}
        />
      )}
    </div>
  );
};

export default EditionComposer;
